import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FaBox, FaTools, FaClock, FaMapMarkerAlt, FaEye, FaUser, 
  FaPaperPlane, FaPaperclip, FaFilePdf, FaImage, FaTimes,
  FaComments, FaSpinner
} from 'react-icons/fa';

export default function RequestDetail() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);

  useEffect(() => {
    fetchRequest();
  }, [id, user]);

  useEffect(() => {
    if (request && user) {
      fetchConversation();
    }
  }, [request, user]);

  useEffect(() => {
    // Only scroll to bottom on initial load or when new messages are added
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      previousMessageCount.current = messages.length;
    } else if (messages.length > previousMessageCount.current) {
      scrollToBottom();
      previousMessageCount.current = messages.length;
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchRequest = async () => {
    try {
      let url = `${API}/requests/public/${id}`;
      if (user) {
        url += `?user_id=${user.id}`;
      }
      
      const response = await axios.get(url);
      setRequest(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching request:', error);
      setLoading(false);
    }
  };

  const fetchConversation = async () => {
    if (!user) return;
    
    setLoadingChat(true);
    try {
      const response = await axios.get(
        `${API}/requests/conversations/by-request/${id}?user_id=${user.id}`
      );
      
      if (response.data && response.data.length > 0) {
        setConversation(response.data[0]);
        setMessages(response.data[0].messages || []);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleStartConversation = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour envoyer un message');
      navigate('/login');
      return;
    }

    if (!newMessage.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/requests/conversations/start?request_id=${id}&user_id=${user.id}&message=${encodeURIComponent(newMessage)}`
      );
      
      toast.success('Message envoyé !');
      setNewMessage('');
      
      // Fetch the new conversation
      await fetchConversation();
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !attachment) {
      toast.error('Veuillez écrire un message ou joindre un fichier');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', newMessage || ' ');
      if (attachment) {
        formData.append('attachment', attachment);
      }

      await axios.post(
        `${API}/requests/conversations/${conversation.id}/message?user_id=${user.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setNewMessage('');
      setAttachment(null);
      
      // Refresh conversation
      await fetchConversation();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo)');
        return;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Type de fichier non autorisé. Formats acceptés: PDF, JPG, PNG, GIF');
        return;
      }
      
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOwnMessage = (msg) => {
    return msg.sender_id === user?.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">
          {i18n.language === 'fr' ? 'Demande non trouvée' : 'Request not found'}
        </p>
      </div>
    );
  }

  const isOwner = user?.id === request.user_id;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <Link to="/requests" className="text-[#0A2540] hover:underline mb-4 inline-block">
          ← {i18n.language === 'fr' ? 'Retour aux demandes' : 'Back to requests'}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {/* Images */}
                {request.images && request.images.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
                    {request.images.map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        alt={`${i18n.language === 'fr' ? request.title_fr : request.title_en} - ${idx + 1}`}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}

                <div className="p-6">
                  {/* Type Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    {request.request_type === 'product' ? 
                      <FaBox className="text-2xl" /> : 
                      <FaTools className="text-2xl" />
                    }
                    <span className="px-4 py-2 bg-[#0A2540] text-white rounded-full text-sm font-semibold">
                      {request.request_type === 'product' 
                        ? (i18n.language === 'fr' ? 'Produit' : 'Product')
                        : (i18n.language === 'fr' ? 'Service' : 'Service')
                      }
                    </span>
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <FaEye /> {request.views} {i18n.language === 'fr' ? 'vues' : 'views'}
                    </span>
                  </div>

                  {/* Title */}
                  <h1 className="text-3xl font-bold text-[#0A2540] mb-4">
                    {i18n.language === 'fr' ? request.title_fr : request.title_en}
                  </h1>

                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-lg mb-2">Description</h3>
                    <p className="text-gray-700 leading-relaxed">
                      {i18n.language === 'fr' ? request.description_fr : request.description_en}
                    </p>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {request.quantity && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                        <FaBox className="text-[#635BFF] text-xl" />
                        <div>
                          <p className="text-xs text-gray-500">
                            {i18n.language === 'fr' ? 'Quantité' : 'Quantity'}
                          </p>
                          <p className="font-semibold">
                            {request.quantity} {request.unit || ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {request.deadline && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                        <FaClock className="text-[#00D4FF] text-xl" />
                        <div>
                          <p className="text-xs text-gray-500">
                            {i18n.language === 'fr' ? 'Délai' : 'Deadline'}
                          </p>
                          <p className="font-semibold">{request.deadline}</p>
                        </div>
                      </div>
                    )}

                    {request.budget && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                        <div className="text-[#635BFF] text-xl font-bold">€</div>
                        <div>
                          <p className="text-xs text-gray-500">Budget</p>
                          <p className="font-semibold text-[#635BFF]">
                            {request.budget} €
                          </p>
                        </div>
                      </div>
                    )}

                    {request.location && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                        <FaMapMarkerAlt className="text-red-500 text-xl" />
                        <div>
                          <p className="text-xs text-gray-500">
                            {i18n.language === 'fr' ? 'Localisation' : 'Location'}
                          </p>
                          <p className="font-semibold">{request.location}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Chat */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-4 text-[#0A2540] flex items-center gap-2">
                  <FaComments />
                  {i18n.language === 'fr' ? 'Contacter le demandeur' : 'Contact Requester'}
                </h3>

                {!user ? (
                  /* Not logged in */
                  <div className="text-center py-8">
                    <FaUser className="text-4xl text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">
                      {i18n.language === 'fr' 
                        ? 'Connectez-vous pour envoyer un message'
                        : 'Login to send a message'}
                    </p>
                    <Link to="/login">
                      <Button className="w-full btn-primary">
                        {i18n.language === 'fr' ? 'Se connecter' : 'Login'}
                      </Button>
                    </Link>
                  </div>
                ) : isOwner ? (
                  /* Owner view */
                  <div className="text-center py-8 text-gray-600">
                    <p>{i18n.language === 'fr' ? 'Ceci est votre demande' : 'This is your request'}</p>
                  </div>
                ) : (
                  /* Chat interface */
                  <div className="flex flex-col h-[400px]">
                    {/* Messages area */}
                    <div className="flex-1 overflow-y-auto mb-4 space-y-3 p-2 bg-gray-50 rounded-lg">
                      {loadingChat ? (
                        <div className="flex items-center justify-center h-full">
                          <FaSpinner className="animate-spin text-gray-400 text-2xl" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                          <p>{i18n.language === 'fr' 
                            ? 'Envoyez le premier message...'
                            : 'Send the first message...'}
                          </p>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg, idx) => (
                            <div 
                              key={idx}
                              className={`flex ${isOwnMessage(msg) ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[80%] rounded-lg p-3 ${
                                isOwnMessage(msg) 
                                  ? 'bg-[#0A2540] text-white' 
                                  : 'bg-white border'
                              }`}>
                                <p className={`text-xs mb-1 ${
                                  isOwnMessage(msg) ? 'text-blue-200' : 'text-gray-500'
                                }`}>
                                  {msg.sender_name}
                                </p>
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                
                                {/* Attachments */}
                                {msg.attachments && msg.attachments.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {msg.attachments.map((att, attIdx) => (
                                      <a 
                                        key={attIdx}
                                        href={`${API}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 text-xs p-2 rounded ${
                                          isOwnMessage(msg) 
                                            ? 'bg-blue-600 hover:bg-blue-700' 
                                            : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                      >
                                        {att.type === 'pdf' ? <FaFilePdf /> : <FaImage />}
                                        <span className="truncate">{att.filename}</span>
                                      </a>
                                    ))}
                                  </div>
                                )}
                                
                                <p className={`text-xs mt-1 ${
                                  isOwnMessage(msg) ? 'text-blue-200' : 'text-gray-400'
                                }`}>
                                  {formatDate(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Attachment preview */}
                    {attachment && (
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg mb-2">
                        {attachment.type?.startsWith('image') ? <FaImage /> : <FaFilePdf />}
                        <span className="text-sm truncate flex-1">{attachment.name}</span>
                        <button 
                          onClick={removeAttachment}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    )}

                    {/* Input area */}
                    <div className="space-y-2">
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={i18n.language === 'fr' 
                          ? 'Écrivez votre message...'
                          : 'Write your message...'}
                        className="resize-none"
                        rows={2}
                      />
                      
                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.gif"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-shrink-0"
                        >
                          <FaPaperclip />
                        </Button>
                        <Button
                          onClick={conversation ? handleSendMessage : handleStartConversation}
                          disabled={sending || (!newMessage.trim() && !attachment)}
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                        >
                          {sending ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <>
                              <FaPaperPlane className="mr-2" />
                              {i18n.language === 'fr' ? 'Envoyer' : 'Send'}
                            </>
                          )}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-gray-500 text-center">
                        📎 PDF, JPG, PNG, GIF (max 10 Mo)
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
